//src/app/services/taxEstimates/index.ts
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from '../index';
import { IUser } from '../user';

export interface ITaxEstimate {
  id: number;
  year: number;
  type: 'business' | 'individual';
  status: 'in_progress' | 'completed' | 'submitted' | 'cancelled';
  title: string;
  creator: IUser;
  business_id?: number;
  business?: { id: number; name: string };
  created_at: string;
  price?: number;
  estimated_fees_cents?: number;
  data?: ITaxEstimateForm;
}

export interface ITaxEstimateForm {
  type: 'individual' | 'business';
  business_id?: number;

  year: number;
  firstName: string;
  lastName: string;
  ssn: string;
  phoneNumber: string;
  email: string;

  isMarried?: 'yes' | 'no';
  spouseFirstName?: string;
  spouseLastName?: string;
  spouseSSN?: string;

  haveDependents?: 'yes' | 'no';
  depenendentsCountUnder16?: number;
  otherdepenendentsCount?: number;

  address: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
  liveOutsideOfUs: boolean;
  otherCountry: string;
  provinceCounty: string;
  collegeTuitionAmount: number;
  businessType?: string;
  expectedBusinessProfitOrLoss: number;
  expectedBusinessProfitOrLossFromPersonallyOwnedRentalProperty: number;
  expectedIraOrPensionDistribution: number;
  federalWithholdingsOnIra: number;
  stateWithholdingsOnIra: number;
  taxpayerTotalSocialSecurityReceivedSoFarThisYear: number;
  spouseTotalSocialSecurityReceivedSoFarThisYear: number;
  taxpayerTotalUnemploymentCompensationReceivedSoFarThisYear: number;
  spouseTotalUnemploymentCompensationReceivedSoFarThisYear: number;
  otherIncome: number;

  federalEstimatedTaxesAlreadyPaidSoFarThisYear: number;
  stateEstimatedTaxesAlreadyPaidSoFarThisYear: number;
  notes: string;
  reachPhoneNumber: string;
  whatIsTheBestTimeToReachYouAtThisNumber: string;
  agreement: boolean;

  latestPaySub: {
    files: {
      file_url: string;
      title: string;
    };
  }[];

  priorYearTaxReturn: {
    files: {
      file_url: string;
      title: string;
    };
  }[];
}

export const TaxEstimateApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTaxEstimates: builder.query<
      IPaginatedRepsonse<ITaxEstimate>,
      IGetQueryParams<{
        status?: ITaxEstimate['status'];
        client_id?: number;
        business_id?: number;
      }>
    >({
      query: (params) =>
        'tax_fees_estimation_requests' + buildQueryParams(params),
    }),

    getTaxEstimateById: builder.query<ITaxEstimate, number>({
      query: (id) => `/tax_fees_estimation_requests/${id}`,
      transformResponse: (response: { request: ITaxEstimate }) =>
        response.request,
    }),

    createTaxEstimate: builder.mutation<
      Partial<ITaxEstimate>,
      Partial<ITaxEstimate>
    >({
      query: (request) => {
        return {
          url: `/tax_fees_estimation_requests`,
          method: 'POST',
          body: { request },
        };
      },
      transformResponse: (response: { request: ITaxEstimate }) =>
        response.request,
    }),

    updateTaxEstimate: builder.mutation<ITaxEstimate, Partial<ITaxEstimate>>({
      query: (request: Partial<ITaxEstimate>) => {
        const body = {
          request,
        };
        return {
          url: `/tax_fees_estimation_requests/${body.request.id}`,
          method: 'PUT',
          body,
        };
      },

      transformResponse: (response: { request: ITaxEstimate }) =>
        response.request,

      onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => {
        return queryFulfilled.then((response) => {
          dispatch(
            TaxEstimateApi.util.updateQueryData(
              'getTaxEstimateById',
              id!,
              (draft) => Object.assign(draft, response.data),
            ),
          );
        });
      },
    }),

    updateTaxEstimateStatus: builder.mutation<
      ITaxEstimate,
      { id: number; status: ITaxEstimate['status'] }
    >({
      query: ({ id, status }) => {
        let route = '';
        if (status === 'in_progress') route = 'set_as_in_progress';
        if (status === 'completed') route = 'complete';
        if (status === 'cancelled') route = 'cancel';

        return {
          url: `/tax_fees_estimation_requests/${id}/${route}`,
          method: 'POST',
        };
      },

      transformResponse: (response: { request: ITaxEstimate }) =>
        response.request,

      onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => {
        return queryFulfilled.then((response) => {
          dispatch(
            TaxEstimateApi.util.updateQueryData(
              'getTaxEstimateById',
              id!,
              (draft) => Object.assign(draft, response.data),
            ),
          );
        });
      },
    }),

    deleteTaxEstimate: builder.mutation<ITaxEstimate, number>({
      query: (id) => {
        return {
          url: `/tax_fees_estimation_requests/${id}`,
          method: 'Delete',
        };
      },

      transformResponse: (response: { task: ITaxEstimate }) => response.task,
    }),
  }),
});

export const {
  useUpdateTaxEstimateStatusMutation,
  useLazyGetTaxEstimatesQuery,
  useUpdateTaxEstimateMutation,
  useGetTaxEstimateByIdQuery,
  useDeleteTaxEstimateMutation,
  useCreateTaxEstimateMutation,
} = TaxEstimateApi;
